(() => {
  if (customElements.get('frequently-bought-together')) return;

  const selectors = {
    mainJson: '[data-fbt-main-json]',
    mainImage: '[data-fbt-main-image]',
    mainPrice: '[data-fbt-main-price]',
    mainVariant: '[data-fbt-main-variant]',
    item: '[data-fbt-item]',
    productJson: '[data-fbt-product-json]',
    checkbox: '[data-fbt-checkbox]',
    variantSelect: '[data-fbt-variant-select]',
    image: '[data-fbt-image]',
    price: '[data-fbt-price]',
    total: '[data-fbt-total]',
    submit: '[data-fbt-submit]',
    buttonText: '[data-fbt-button-text]',
    message: '[data-fbt-message]',
    productForm: '[data-product-form]',
  };

  class FrequentlyBoughtTogether extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.mainData = this.readJson(selectors.mainJson);
      if (!this.mainData) return;

      this.mainVariants = this.mainData.variants || [];
      this.mainVariant = this.mainVariants.find((variant) => String(variant.id) === String(this.dataset.mainVariantId)) || this.mainVariants[0];
      this.items = Array.from(this.querySelectorAll(selectors.item)).map((card) => this.buildItem(card)).filter(Boolean);

      this.mainImage = this.querySelector(selectors.mainImage);
      this.mainPrice = this.querySelector(selectors.mainPrice);
      this.mainVariantTitle = this.querySelector(selectors.mainVariant);
      this.totalEl = this.querySelector(selectors.total);
      this.submit = this.querySelector(selectors.submit);
      this.buttonText = this.querySelector(selectors.buttonText);
      this.message = this.querySelector(selectors.message);
      this.ctaText = this.dataset.ctaText || '';
      this.showTotalInCta = this.dataset.showTotal === 'true';
      this.isAdding = false;

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      document.addEventListener('theme:variant:change', this.onVariantChange);
      this.bindMainIdInput();
      this.bindItems();
      this.submit?.addEventListener('click', this.onSubmit);
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:variant:change', this.onVariantChange);
      this.mainIdInput?.removeEventListener('change', this.onMainIdInputChange);
      this.submit?.removeEventListener('click', this.onSubmit);
      this.items?.forEach((item) => {
        item.checkbox?.removeEventListener('change', item.onCheckboxChange);
        item.select?.removeEventListener('change', item.onVariantChange);
        item.card?.removeEventListener('click', item.onCardClick);
      });
      this.initialized = false;
    }

    readJson(selector, context = this) {
      const script = context.querySelector(selector);
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('Frequently bought together JSON error:', error);
        return null;
      }
    }

    bindMainIdInput() {
      const form = this.getProductForm();
      this.mainIdInput = form?.querySelector('input[name="id"]');
      this.onMainIdInputChange = () => {
        const variant = this.mainVariants.find((item) => String(item.id) === String(this.mainIdInput.value));
        if (variant) {
          this.mainVariant = variant;
          this.update();
        }
      };
      this.mainIdInput?.addEventListener('change', this.onMainIdInputChange);
    }

    buildItem(card) {
      const product = this.readJson(selectors.productJson, card);
      const checkbox = card.querySelector(selectors.checkbox);
      const select = card.querySelector(selectors.variantSelect);
      const variants = product?.variants || [];
      const variant = variants.find((item) => String(item.id) === String(select?.value)) || variants[0];

      if (!product || !checkbox || !select || !variant) return null;

      return {
        card,
        product,
        checkbox,
        select,
        variants,
        variant,
        image: card.querySelector(selectors.image),
        price: card.querySelector(selectors.price),
      };
    }

    bindItems() {
      this.items.forEach((item) => {
        item.onCheckboxChange = () => {
          this.updateItemState(item);
          this.update();
        };

        item.onVariantChange = () => {
          item.variant = item.variants.find((variant) => String(variant.id) === String(item.select.value)) || item.variant;
          this.updateItemVariant(item);
          this.update();
        };

        item.onCardClick = (event) => {
          if (event.target.closest('select, input, label, button, a')) return;
          item.checkbox.checked = !item.checkbox.checked;
          item.checkbox.dispatchEvent(new Event('change', {bubbles: true}));
        };

        item.checkbox.addEventListener('change', item.onCheckboxChange);
        item.select.addEventListener('change', item.onVariantChange);
        item.card.addEventListener('click', item.onCardClick);
        this.updateItemVariant(item);
        this.updateItemState(item);
      });
    }

    getProductForm() {
      return document.querySelector(selectors.productForm);
    }

    onVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant || !this.mainVariants.some((item) => String(item.id) === String(variant.id))) return;

      this.mainVariant = {
        ...this.mainVariants.find((item) => String(item.id) === String(variant.id)),
        ...variant,
      };
      this.updateMainVariant();
      this.update();
    }

    updateItemVariant(item) {
      if (!item.variant) return;

      if (item.price) {
        item.price.innerHTML = this.formatMoney(item.variant.price);
      }

      if (item.image && item.variant.image) {
        item.image.src = item.variant.image;
        item.image.srcset = '';
      }

      const available = Boolean(item.variant.available);
      item.checkbox.disabled = !available;
      item.card.classList.toggle('is-unavailable', !available);
      if (!available) item.checkbox.checked = false;
      this.updateItemState(item);
    }

    updateItemState(item) {
      item.card.classList.toggle('is-selected', item.checkbox.checked && !item.checkbox.disabled);
    }

    updateMainVariant() {
      if (!this.mainVariant) return;

      if (this.mainImage && this.mainVariant.image) {
        this.mainImage.src = this.mainVariant.image;
        this.mainImage.srcset = '';
      }

      if (this.mainPrice) {
        this.mainPrice.innerHTML = this.formatMoney(this.mainVariant.price);
      }

      if (this.mainVariantTitle) {
        this.mainVariantTitle.textContent = this.mainVariant.title;
        this.mainVariantTitle.classList.toggle('is-hidden', this.mainData.hasOnlyDefaultVariant || this.mainVariant.title === 'Default Title');
      }
    }

    update() {
      this.updateMainVariant();
      const total = this.getSelectedTotal();
      const formattedTotal = this.formatMoney(total);

      if (this.totalEl) {
        this.totalEl.textContent = formattedTotal;
      }

      if (this.buttonText) {
        this.buttonText.textContent = this.showTotalInCta ? `${this.ctaText} · ${formattedTotal}` : this.ctaText;
      }

      if (this.submit) {
        this.submit.disabled = !this.mainVariant?.available || this.isAdding;
      }
    }

    getSelectedTotal() {
      const mainPrice = this.mainVariant?.available ? Number(this.mainVariant.price) : 0;
      return this.items.reduce((total, item) => {
        if (!item.checkbox.checked || item.checkbox.disabled || !item.variant?.available) return total;
        return total + Number(item.variant.price || 0);
      }, mainPrice);
    }

    formatMoney(cents) {
      if (window.theme?.formatMoney) {
        return Number(cents) === 0 ? window.theme.strings.free : window.theme.formatMoney(cents, window.theme.moneyFormat);
      }

      return String(cents);
    }

    getMainLineItem() {
      const form = this.getProductForm();
      const formData = form ? new FormData(form) : new FormData();
      const id = this.mainVariant?.id || formData.get('id');
      const quantity = Number.parseInt(formData.get('quantity'), 10) || 1;
      const item = {
        id: Number(id),
        quantity,
      };
      const properties = {};

      formData.forEach((value, key) => {
        const propertyMatch = key.match(/^properties\[(.+)\]$/);
        if (propertyMatch && value !== '') {
          properties[propertyMatch[1]] = value;
        }
      });

      const sellingPlan = formData.get('selling_plan');
      if (Object.keys(properties).length) item.properties = properties;
      if (sellingPlan) item.selling_plan = Number(sellingPlan);

      return item;
    }

    getPayloadItems() {
      const mainItem = this.getMainLineItem();
      const seen = new Set([String(mainItem.id)]);
      const items = [mainItem];

      this.items.forEach((item) => {
        if (!item.checkbox.checked || item.checkbox.disabled || !item.variant?.available) return;
        const id = String(item.variant.id);
        if (seen.has(id)) return;
        seen.add(id);
        items.push({id: Number(item.variant.id), quantity: 1});
      });

      return items;
    }

    async onSubmit() {
      if (this.isAdding || !this.mainVariant?.available) return;

      const items = this.getPayloadItems();
      if (!items.length) return;

      this.setLoading(true);
      this.setMessage('');

      try {
        const response = await fetch(window.theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
          },
          body: JSON.stringify({items}),
        });

        const data = await response.json();
        if (!response.ok || data.status) {
          throw data;
        }

        document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));

        if (window.theme?.settings?.cartType === 'drawer') {
          document.querySelector('cart-drawer')?.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
        }

        document.dispatchEvent(
          new CustomEvent('theme:product:add', {
            bubbles: true,
            detail: {
              response: data,
              button: this.submit,
            },
          })
        );

        this.setMessage(this.getTranslation('added'), true);
      } catch (error) {
        const message = error?.description || error?.message || this.getTranslation('error');
        this.setMessage(message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.isAdding = isLoading;
      this.classList.toggle('is-loading', isLoading);
      if (this.submit) {
        this.submit.disabled = isLoading || !this.mainVariant?.available;
        this.submit.classList.toggle('is-loading', isLoading);
      }
      this.update();
    }

    setMessage(text, success = false) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.classList.toggle('is-success', success);
    }

    getTranslation(key) {
      const fallback = {
        added: 'Aggiunti al carrello',
        error: "Errore nell'aggiunta al carrello",
      };
      return window.theme?.strings?.frequentlyBoughtTogether?.[key] || fallback[key];
    }
  }

  customElements.define('frequently-bought-together', FrequentlyBoughtTogether);
})();
